export interface AITool {
  id: string
  name: string
  category: 'crawl' | 'walk' | 'run'
  subcategory: string
  description: string
  difficulty: 'beginner' | 'intermediate' | 'advanced'
  timeToImplement: string
  monthlyInvestment: string
  roi: 'low' | 'medium' | 'high' | 'very high'
  features: string[]
  bestFor: string[]
  gettingStarted: string
  website?: string
}

export const aiTools: AITool[] = [
  // CRAWL PHASE TOOLS
  {
    id: 'chatgpt-plus',
    name: 'ChatGPT Plus',
    category: 'crawl',
    subcategory: 'AI Assistants',
    description: 'Advanced AI assistant for content creation, analysis, and problem-solving with custom AI assistant building',
    difficulty: 'beginner',
    timeToImplement: '5 minutes',
    monthlyInvestment: '$20',
    roi: 'high',
    features: [
      'Build your own AI assistant',
      'Advanced reasoning',
      'Web browsing capability',
      'Code interpreter',
      'Image generation (DALL-E)',
      'File upload and analysis'
    ],
    bestFor: [
      'Content creation and editing',
      'Custom business assistants',
      'Brainstorming and ideation',
      'Document analysis',
      'Email drafting',
      'Social media content'
    ],
    gettingStarted: 'Sign up for ChatGPT Plus, then use the AI Assistant Builder to create your first custom assistant trained on your business data',
    website: 'https://chat.openai.com'
  },
  {
    id: 'claude-pro',
    name: 'Claude Pro',
    category: 'crawl',
    subcategory: 'AI Assistants',
    description: 'Advanced AI assistant with exceptional reasoning capabilities and large context window',
    difficulty: 'beginner',
    timeToImplement: '5 minutes',
    monthlyInvestment: '$20',
    roi: 'high',
    features: [
      '200K token context window',
      'Advanced reasoning',
      'Code analysis and generation',
      'Document processing',
      'Conversational AI',
      'Research assistance'
    ],
    bestFor: [
      'Long document analysis',
      'Complex reasoning tasks',
      'Code review and debugging',
      'Research and analysis',
      'Writing assistance',
      'Strategic planning'
    ],
    gettingStarted: 'Subscribe to Claude Pro and start with complex documents or coding tasks that require deep analysis',
    website: 'https://claude.ai'
  },
  {
    id: 'google-gemini',
    name: 'Google Gemini Advanced',
    category: 'crawl',
    subcategory: 'AI Assistants',
    description: 'Google\'s advanced AI with deep Google Workspace connection and works with text, images, and voice',
    difficulty: 'beginner',
    timeToImplement: '5 minutes',
    monthlyInvestment: '$20',
    roi: 'high',
    features: [
      'Google Workspace connection',
      'Multimodal understanding',
      'Real-time information access',
      'Gmail and Drive connection',
      'Meeting summarization',
      'Advanced reasoning'
    ],
    bestFor: [
      'Google Workspace automation',
      'Email management',
      'Meeting summaries',
      'Document analysis',
      'Research with web access',
      'Workflow optimization'
    ],
    gettingStarted: 'Subscribe to Gemini Advanced and connect your Google Workspace for automatic meeting summaries and email help',
    website: 'https://gemini.google.com'
  },
  {
    id: 'notion-ai',
    name: 'Notion AI',
    category: 'crawl',
    subcategory: 'Productivity Tools',
    description: 'AI-powered knowledge management and documentation assistant integrated into Notion workspace',
    difficulty: 'beginner',
    timeToImplement: '10 minutes',
    monthlyInvestment: '$10',
    roi: 'medium',
    features: [
      'Content generation within Notion',
      'Text summarization',
      'Writing assistance',
      'Translation capabilities',
      'Brainstorming support',
      'Template creation'
    ],
    bestFor: [
      'Documentation creation',
      'Knowledge base management',
      'Meeting notes',
      'Project planning',
      'Content organization',
      'Team collaboration'
    ],
    gettingStarted: 'Add Notion AI to your existing Notion workspace and use it to enhance your documentation workflow',
    website: 'https://notion.so/ai'
  },
  {
    id: 'grammarly',
    name: 'Grammarly Business',
    category: 'crawl',
    subcategory: 'Writing Tools',
    description: 'AI-powered writing assistant for professional communication and content creation',
    difficulty: 'beginner',
    timeToImplement: '5 minutes',
    monthlyInvestment: '$15',
    roi: 'medium',
    features: [
      'Grammar and spell checking',
      'Tone detection',
      'Clarity suggestions',
      'Brand voice consistency',
      'Plagiarism detection',
      'Team style guides'
    ],
    bestFor: [
      'Professional emails',
      'Marketing content',
      'Proposals and reports',
      'Social media posts',
      'Website copy',
      'Team communication'
    ],
    gettingStarted: 'Install Grammarly browser extension and desktop app, then set up your brand voice preferences',
    website: 'https://grammarly.com/business'
  },

  // WALK PHASE TOOLS
  {
    id: 'zapier',
    name: 'Zapier',
    category: 'walk',
    subcategory: 'Automation Platforms',
    description: 'No-code automation platform connecting 6,000+ apps with AI-enhanced workflow capabilities',
    difficulty: 'intermediate',
    timeToImplement: '1-2 hours',
    monthlyInvestment: '$20-100',
    roi: 'very high',
    features: [
      '6,000+ app connections',
      'Multi-step workflows',
      'AI-powered automation',
      'Conditional logic',
      'Data formatting',
      'Error handling'
    ],
    bestFor: [
      'Lead management automation',
      'Email marketing workflows',
      'Data synchronization',
      'Social media automation',
      'Customer onboarding',
      'Invoice generation'
    ],
    gettingStarted: 'Start with a simple contact form to email workflow, then gradually build more complex automations',
    website: 'https://zapier.com'
  },
  {
    id: 'n8n',
    name: 'n8n',
    category: 'walk',
    subcategory: 'Automation Platforms',
    description: 'Open-source workflow automation with advanced customization and self-hosting options',
    difficulty: 'intermediate',
    timeToImplement: '2-4 hours',
    monthlyInvestment: '$22-50',
    roi: 'very high',
    features: [
      '1,000+ app connections',
      'Visual workflow editor',
      'Custom JavaScript/Python',
      'Self-hosting option',
      'Advanced logic',
      'App connections'
    ],
    bestFor: [
      'Complex business workflows',
      'Custom connections',
      'Data privacy requirements',
      'Advanced automation logic',
      'Enterprise workflows',
      'Developer-friendly automation'
    ],
    gettingStarted: 'Sign up for n8n cloud or self-host, then build your first workflow connecting two business apps',
    website: 'https://n8n.io'
  },
  {
    id: 'calendly',
    name: 'Calendly',
    category: 'walk',
    subcategory: 'Scheduling Tools',
    description: 'Automated scheduling platform with intelligent meeting coordination and works with other apps',
    difficulty: 'beginner',
    timeToImplement: '30 minutes',
    monthlyInvestment: '$12',
    roi: 'high',
    features: [
      'Automatic scheduling',
      'Calendar connection',
      'Meeting preferences',
      'Reminder automation',
      'Team scheduling',
      'Works with other apps'
    ],
    bestFor: [
      'Client consultations',
      'Sales meetings',
      'Team coordination',
      'Event scheduling',
      'Customer support',
      'Interview scheduling'
    ],
    gettingStarted: 'Connect your calendar, set availability preferences, and embed booking links in your emails',
    website: 'https://calendly.com'
  },
  {
    id: 'hubspot',
    name: 'HubSpot CRM',
    category: 'walk',
    subcategory: 'CRM Systems',
    description: 'AI-powered customer relationship management with automation and marketing connections',
    difficulty: 'intermediate',
    timeToImplement: '1 week',
    monthlyInvestment: '$50',
    roi: 'very high',
    features: [
      'Contact management',
      'Deal tracking',
      'Email automation',
      'Lead scoring',
      'Reporting dashboard',
      'Works with other apps'
    ],
    bestFor: [
      'Sales pipeline management',
      'Customer communication',
      'Lead nurturing',
      'Marketing automation',
      'Performance tracking',
      'Team collaboration'
    ],
    gettingStarted: 'Import your contacts, set up your sales pipeline, and create your first automated email sequence',
    website: 'https://hubspot.com'
  },
  {
    id: 'mailchimp',
    name: 'Mailchimp',
    category: 'walk',
    subcategory: 'Email Marketing',
    description: 'AI-enhanced email marketing platform with automation and audience insights',
    difficulty: 'intermediate',
    timeToImplement: '2-3 hours',
    monthlyInvestment: '$20-50',
    roi: 'high',
    features: [
      'Email automation',
      'Audience segmentation',
      'A/B testing',
      'Analytics dashboard',
      'Social media connections',
      'E-commerce tracking'
    ],
    bestFor: [
      'Newsletter campaigns',
      'Customer nurturing',
      'Product launches',
      'Event promotion',
      'Customer retention',
      'Lead generation'
    ],
    gettingStarted: 'Upload your contact list, design your first email template, and set up a welcome automation',
    website: 'https://mailchimp.com'
  },

  // RUN PHASE TOOLS
  {
    id: 'salesforce-einstein',
    name: 'Salesforce Einstein',
    category: 'run',
    subcategory: 'Enterprise AI',
    description: 'Advanced AI platform integrated into Salesforce for predictive analytics and automation',
    difficulty: 'advanced',
    timeToImplement: '1-3 months',
    monthlyInvestment: '$500+',
    roi: 'very high',
    features: [
      'Predictive lead scoring',
      'Opportunity insights',
      'AI-powered forecasting',
      'Automated data entry',
      'Intelligent recommendations',
      'Custom AI models'
    ],
    bestFor: [
      'Enterprise sales operations',
      'Predictive analytics',
      'Customer lifetime value',
      'Churn prediction',
      'Revenue forecasting',
      'Advanced automation'
    ],
    gettingStarted: 'Requires Salesforce implementation. Start with Einstein Lead Scoring and gradually add more AI features',
    website: 'https://salesforce.com/products/einstein'
  },
  {
    id: 'tableau',
    name: 'Tableau',
    category: 'run',
    subcategory: 'Business Intelligence',
    description: 'Advanced data visualization and business intelligence platform with AI-powered insights',
    difficulty: 'advanced',
    timeToImplement: '2-4 weeks',
    monthlyInvestment: '$70',
    roi: 'high',
    features: [
      'Advanced data visualization',
      'Real-time dashboards',
      'Predictive analytics',
      'Natural language queries',
      'Mobile accessibility',
      'Works with business systems'
    ],
    bestFor: [
      'Business intelligence',
      'Data analysis',
      'Performance dashboards',
      'Trend identification',
      'Executive reporting',
      'Data-driven decisions'
    ],
    gettingStarted: 'Connect your data sources, create your first visualization, and build interactive dashboards',
    website: 'https://tableau.com'
  },
  {
    id: 'power-bi',
    name: 'Microsoft Power BI',
    category: 'run',
    subcategory: 'Business Intelligence',
    description: 'Microsoft\'s business intelligence platform with AI features and Office 365 connection',
    difficulty: 'advanced',
    timeToImplement: '2-4 weeks',
    monthlyInvestment: '$10-20',
    roi: 'high',
    features: [
      'Data visualization',
      'AI insights',
      'Real-time monitoring',
      'Office 365 connection',
      'Custom dashboards',
      'Mobile apps'
    ],
    bestFor: [
      'Microsoft ecosystem users',
      'Financial reporting',
      'Operations dashboards',
      'KPI tracking',
      'Team collaboration',
      'Automated insights'
    ],
    gettingStarted: 'Connect to your Microsoft 365 data, create reports, and share dashboards with your team',
    website: 'https://powerbi.microsoft.com'
  },
  {
    id: 'anthropic-claude-api',
    name: 'Claude App Connections',
    category: 'run',
    subcategory: 'AI Development',
    description: 'Enterprise-grade AI app connections for building custom applications with advanced reasoning capabilities',
    difficulty: 'advanced',
    timeToImplement: '1-6 months',
    monthlyInvestment: '$100+',
    roi: 'very high',
    features: [
      'Advanced reasoning connections',
      'Large context windows',
      'Custom connections',
      'Scalable infrastructure',
      'Enterprise security',
      'Developer tools'
    ],
    bestFor: [
      'Custom AI applications',
      'Document processing',
      'Customer service automation',
      'Content generation systems',
      'Research automation',
      'Decision support systems'
    ],
    gettingStarted: 'Requires development expertise. Start with app connection documentation and build a proof of concept',
    website: 'https://console.anthropic.com'
  },
  {
    id: 'openai-api',
    name: 'OpenAI App Connections',
    category: 'run',
    subcategory: 'AI Development',
    description: 'Comprehensive AI app connection platform for building applications with GPT-4 and other AI models',
    difficulty: 'advanced',
    timeToImplement: '1-6 months',
    monthlyInvestment: '$100+',
    roi: 'very high',
    features: [
      'GPT-4 app connections',
      'Image generation',
      'Code interpreter',
      'Fine-tuning capabilities',
      'Smart text matching',
      'Moderation tools'
    ],
    bestFor: [
      'Custom chatbots',
      'Content automation',
      'Code generation',
      'Image creation',
      'Search enhancement',
      'AI-powered features'
    ],
    gettingStarted: 'Requires development skills. Start with the playground, then connect with your applications',
    website: 'https://platform.openai.com'
  }
]

export const toolCategories = {
  crawl: {
    title: 'CRAWL Phase',
    subtitle: 'Quick wins with existing tools',
    description: 'Start your AI journey with immediate, low-cost solutions that deliver value in minutes',
    timeframe: 'Hours to implement',
    investment: '$10-20/month',
    difficulty: 'Beginner-friendly'
  },
  walk: {
    title: 'WALK Phase', 
    subtitle: 'Connect apps with no-code automation',
    description: 'Build automated workflows that connect your business applications without coding',
    timeframe: 'Days to implement',
    investment: '$20-200/month',
    difficulty: 'Intermediate'
  },
  run: {
    title: 'RUN Phase',
    subtitle: 'AI built into everything',
    description: 'Advanced AI solutions that create competitive advantages by building AI into everything',
    timeframe: 'Weeks to months',
    investment: '$100+ and/or expert help',
    difficulty: 'Advanced'
  }
}

export const subcategories = {
  'AI Assistants': {
    description: 'General-purpose AI tools for content creation, analysis, and problem-solving',
    icon: 'Brain'
  },
  'Productivity Tools': {
    description: 'AI-enhanced tools for documentation, organization, and workflow management',
    icon: 'Lightbulb'
  },
  'Writing Tools': {
    description: 'AI-powered writing assistance for professional communication',
    icon: 'FileText'
  },
  'Automation Platforms': {
    description: 'No-code platforms for connecting apps and automating business processes',
    icon: 'Workflow'
  },
  'Scheduling Tools': {
    description: 'Intelligent scheduling and calendar management systems',
    icon: 'Calendar'
  },
  'CRM Systems': {
    description: 'Customer relationship management with AI-powered insights',
    icon: 'Users'
  },
  'Email Marketing': {
    description: 'AI-enhanced email marketing and customer communication platforms',
    icon: 'Mail'
  },
  'Enterprise AI': {
    description: 'Advanced AI platforms for large-scale business operations',
    icon: 'Building'
  },
  'Business Intelligence': {
    description: 'Data visualization and analytics platforms with AI insights',
    icon: 'BarChart3'
  },
  'AI Development': {
    description: 'App connections and platforms for building custom AI applications',
    icon: 'Code'
  }
}